import { Switch as HeadlessSwitch } from '@headlessui/react'
import { ComponentPropsWithoutRef, forwardRef } from 'react'

import { cn } from '@sparkai/utils'

export interface SwitchProps extends Omit<ComponentPropsWithoutRef<'button'>, 'onChange'> {
  checked?: boolean
  defaultChecked?: boolean
  onChange?: (checked: boolean) => void
  /** For accessibility */
  description?: string
  name?: string
  /** For usage with name */
  value?: string
  labelClassName?: string
  groupClassName?: string
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onChange, description, className, labelClassName, groupClassName, disabled, ...props },
  ref
) {
  return (
    <HeadlessSwitch.Group>
      <div className={cn('flex items-center gap-2', groupClassName)}>
        <HeadlessSwitch
          ref={ref}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className={cn(
            checked ? 'bg-blue-500 disabled:bg-blue-300' : 'bg-gray-300 disabled:to-gray-200',
            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent',
            'transition-colors duration-200 ease-in-out',
            'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden disabled:cursor-not-allowed',
            className
          )}
          {...props}
        >
          <span
            aria-hidden='true'
            className={cn(
              checked ? 'translate-x-5' : 'translate-x-0',
              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0',
              'transition duration-200 ease-in-out'
            )}
          />
        </HeadlessSwitch>
        <HeadlessSwitch.Label
          className={cn(
            'mr-4 w-max text-xs font-medium',
            {
              'text-gray-400': disabled,
              'text-gray-500': !disabled,
            },
            labelClassName
          )}
        >
          {description}
        </HeadlessSwitch.Label>
      </div>
    </HeadlessSwitch.Group>
  )
})
